import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api";
import { Lesson, Child } from "../types";
import VoxelBackground from "../components/VoxelBackground";

export default function ChildView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState<Child | null>(null);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGrade, setSelectedGrade] = useState<number | null>(null);
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [completedLessons, setCompletedLessons] = useState<string[]>([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const childRes = await api.get("/children");
      const found = childRes.data.children.find((c: Child) => c.id === id);
      setChild(found);
      const lessonsRes = await api.get("/lessons");
      setLessons(lessonsRes.data.lessons);
      const completedPerfect = found?.progress
      ?.filter((p: any) => p.completed)
      ?.map((p: any) => p.lessonId) || [];
      setCompletedLessons(completedPerfect);
    } catch {
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div style={styles.loading}>⏳ Ładowanie...</div>;

  const gradeColors: Record<number, string> = {
    1: "#667eea",
    2: "#f093fb",
    3: "#4facfe",
  };

  if (!selectedGrade) {
    return (
      <div style={styles.container}>
        <VoxelBackground />
        <div style={styles.header}>
          <button onClick={() => navigate("/dashboard")} style={styles.backBtn}>← Wróć</button>
          <h1 style={styles.logo}>🧮 Mathix</h1>
          <div style={styles.stats}>⭐ {child?.points} pkt</div>
        </div>
        <div style={styles.content}>
          <div style={styles.heroSection}>
            <div style={styles.robotBig}>🤖</div>
            <h2 style={styles.greeting}>Cześć, {child?.name}! Wybierz klasę!</h2>
          </div>
          <div style={styles.gradeGrid}>
            <div style={{...styles.gradeCard, background: "linear-gradient(135deg, #667eea, #764ba2)"}} onClick={() => setSelectedGrade(1)}>
              <div style={styles.gradeEmoji}>🌱</div>
              <h2 style={styles.gradeTitle}>Klasa 1</h2>
              <p style={styles.gradeDesc}>Liczenie, dodawanie, odejmowanie</p>
              <div style={styles.gradeCount}>{lessons.filter(l => l.grade === 1).length} lekcji</div>
            </div>
            <div style={{...styles.gradeCard, background: "linear-gradient(135deg, #f093fb, #f5576c)"}} onClick={() => setSelectedGrade(2)}>
              <div style={styles.gradeEmoji}>🌿</div>
              <h2 style={styles.gradeTitle}>Klasa 2</h2>
              <p style={styles.gradeDesc}>Mnożenie, dzielenie, pieniądze</p>
              <div style={styles.gradeCount}>{lessons.filter(l => l.grade === 2).length} lekcji</div>
            </div>
            <div style={{...styles.gradeCard, background: "linear-gradient(135deg, #4facfe, #00f2fe)"}} onClick={() => setSelectedGrade(3)}>
              <div style={styles.gradeEmoji}>🌳</div>
              <h2 style={styles.gradeTitle}>Klasa 3</h2>
              <p style={styles.gradeDesc}>Tabliczka mnożenia, ułamki, zadania</p>
              <div style={styles.gradeCount}>{lessons.filter(l => l.grade === 3).length} lekcji</div>
            </div>
            <div style={{...styles.gradeCard, background: "linear-gradient(135deg, #00c853, #00897b)"}} onClick={() => { setSelectedGrade(0); setSelectedSubject("CODING"); }}>
              <div style={styles.gradeEmoji}>💻</div>
              <h2 style={styles.gradeTitle}>Kodowanie</h2>
              <p style={styles.gradeDesc}>Algorytmy i podstawy programowania</p>
              <div style={styles.gradeCount}>{lessons.filter(l => l.subject === "CODING").length} lekcji</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const filteredLessons = lessons
    .filter(l => selectedSubject === "CODING"
      ? l.subject === "CODING"
      : l.grade === selectedGrade && l.subject === "MATH"
    )
    .sort((a, b) => a.order - b.order);

  const gradeTitles: Record<number, string> = {
    1: "📚 Matematyka — Klasa 1",
    2: "📚 Matematyka — Klasa 2",
    3: "📚 Matematyka — Klasa 3",
    0: "💻 Kodowanie",
  };

  const isLessonUnlocked = (lesson: Lesson, index: number) => {
    if (index === 0) return true;
    const previousLesson = filteredLessons[index - 1];
    return completedLessons.includes(previousLesson.id);
  };

  return (
    <div style={styles.container}>
      <VoxelBackground />
      <div style={styles.header}>
        <button onClick={() => { setSelectedGrade(null); setSelectedSubject(null); }} style={styles.backBtn}>← Wróć</button>
        <h1 style={styles.logo}>🧮 Mathix</h1>
        <div style={styles.stats}>⭐ {child?.points} pkt</div>
      </div>
      <div style={styles.content}>
        <h2 style={{...styles.greeting, color: gradeColors[selectedGrade] || "#00c853", textAlign: "center", marginBottom: "32px"}}>
          {gradeTitles[selectedGrade]}
        </h2>
        <div style={styles.lessonsGrid}>
          {filteredLessons.map((lesson, index) => {
            const unlocked = isLessonUnlocked(lesson, index);
            return (
              <div
                key={lesson.id}
                style={{
                  ...styles.lessonCard,
                  borderTop: `4px solid ${unlocked ? gradeColors[lesson.grade] || "#00c853" : "#ccc"}`,
                  opacity: unlocked ? 1 : 0.7,
                  cursor: unlocked ? "pointer" : "not-allowed",
                }}
                onClick={() => unlocked && navigate(`/lesson/${lesson.id}/${id}`)}
              >
                <div style={styles.lessonIcon}>
                  {!unlocked ? "🔒" : lesson.subject === "CODING" ? "💻" : lesson.grade === 1 ? "🔢" : lesson.grade === 2 ? "➕" : "🧮"}
                </div>
                <h4 style={styles.lessonTitle}>{lesson.title}</h4>
                <p style={styles.lessonDesc}>{lesson.description}</p>
                <div style={styles.lessonMeta}>
                  <span style={{
                    ...styles.gradeBadge,
                    background: unlocked ? `${gradeColors[lesson.grade] || "#00c853"}22` : "#f5f5f5",
                    color: unlocked ? gradeColors[lesson.grade] || "#00c853" : "#999",
                  }}>
                    {lesson.subject === "CODING" ? "Kodowanie" : `Klasa ${lesson.grade}`}
                  </span>
                  <span style={styles.pointsBadge}>
                    +{(lesson.content as any).pointsReward} pkt
                  </span>
                </div>
                {unlocked ? (
                  <button style={{
                    ...styles.startBtn,
                    background: completedLessons.includes(lesson.id)
                      ? "#4caf50"
                      : gradeColors[lesson.grade] || "#00c853",
                  }}>
                    {completedLessons.includes(lesson.id) ? "✅ Ukończona! Zagraj znowu" : "Zacznij! 🚀"}
                  </button>
                ) : (
                  <button style={{...styles.startBtn, background: "#ccc", cursor: "not-allowed"}}>
                    🔒 Ukończ poprzednią lekcję!
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: "100vh",
    background: "#87CEEB",
    position: "relative",
    overflow: "hidden",
  },
  header: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    padding: "16px 32px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "relative",
    zIndex: 2,
  },
  backBtn: {
    background: "rgba(255,255,255,0.2)",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "8px 16px",
    cursor: "pointer",
    fontSize: "16px",
  },
  logo: { color: "white", margin: 0, fontSize: "24px" },
  stats: { color: "white", fontSize: "16px" },
  content: { padding: "32px", position: "relative", zIndex: 1 },
  heroSection: { textAlign: "center", marginBottom: "40px" },
  robotBig: { fontSize: "80px", marginBottom: "16px" },
  greeting: { fontSize: "28px", color: "#333" },
  gradeGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
    gap: "24px",
    maxWidth: "900px",
    margin: "0 auto",
  },
  gradeCard: {
    borderRadius: "24px",
    padding: "32px",
    textAlign: "center",
    cursor: "pointer",
    color: "white",
    boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
  },
  gradeEmoji: { fontSize: "64px", marginBottom: "16px" },
  gradeTitle: { margin: "0 0 8px", fontSize: "28px" },
  gradeDesc: { margin: "0 0 16px", opacity: 0.9, fontSize: "14px" },
  gradeCount: {
    background: "rgba(255,255,255,0.2)",
    borderRadius: "20px",
    padding: "6px 16px",
    display: "inline-block",
    fontSize: "14px",
  },
  lessonsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gap: "20px",
  },
  lessonCard: {
    background: "white",
    borderRadius: "20px",
    padding: "24px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  },
  lessonIcon: { fontSize: "40px", marginBottom: "12px" },
  lessonTitle: { margin: "0 0 8px", fontSize: "18px", color: "#333" },
  lessonDesc: { color: "#888", fontSize: "14px", marginBottom: "16px" },
  lessonMeta: { display: "flex", gap: "8px", marginBottom: "16px" },
  gradeBadge: { borderRadius: "20px", padding: "4px 12px", fontSize: "12px" },
  pointsBadge: {
    background: "#fff3cd",
    color: "#f57f17",
    borderRadius: "20px",
    padding: "4px 12px",
    fontSize: "12px",
  },
  startBtn: {
    width: "100%",
    padding: "12px",
    color: "white",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    fontSize: "16px",
  },
  loading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    fontSize: "24px",
  },
};